/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import {
  MdCode,
  MdAdd,
  MdEdit,
  MdDelete,
  MdCheckCircle,
  MdBlock,
} from "react-icons/md";

import {
  PageHeader,
  DataTable,
  StatusBadge,
  FilterBar,
  ConfirmModal,
} from "../../../components/Shared";

import PermissionGuard from "../../../components/Atoms/PermissionGuard/PermissionGuard";
import { ACTIONS } from "../../../_helpers/usePermission";
import { useListPage } from "../../../hooks/useListPage";

import {
  createHsn,
  getHsnList,
  updateHsn,
  enableDisableHsn,
  softDeleteHsn,
  reviewHsnSubmission,
} from "../../../Redux/productSlice";

import AddHsnModal from "../../ProductManagement/ProductCatalog/components/Modals/AddHsnModal";

/* ==================== FILTER CONFIGURATION ==================== */

const FILTER_FIELDS = [
  {
    key: "isDisable",
    type: "select",
    label: "Status",
    width: "w-36",
    options: [
      { value: "", label: "All Status" },
      { value: "false", label: "Active" },
      { value: "true", label: "Inactive" },
    ],
  },
];

/* ==================== TABLE COLUMNS ==================== */

const COLUMNS = [
  {
    key: "code",
    label: "HSN Code",
    sortable: true,
    headerClassName: "text-center",
    cellClassName: "text-center",
    render: (v) => (
      <span className="font-mono font-semibold text-[var(--admin-navy)]">
        {v}
      </span>
    ),
  },
  {
    key: "gstRate",
    label: "GST Rate",
    headerClassName: "text-center",
    cellClassName: "text-center",
    render: (v, row) => (
      <div className="text-center text-sm">
        <span className="font-semibold text-gray-800">
          {v ?? row.IGST ?? 0}%
        </span>
        <span className="mt-0.5 block text-xs text-gray-500">
          CGST {(Number(v ?? row.IGST ?? 0) / 2).toFixed(2)}% + SGST{" "}
          {(Number(v ?? row.IGST ?? 0) / 2).toFixed(2)}%
        </span>
      </div>
    ),
  },
  {
    key: "cessRate",
    label: "Cess",
    headerClassName: "text-center",
    cellClassName: "text-center",
    render: (v, row) => (
      <span className="block w-full text-center text-sm">
        {v ?? row.additionalTax ?? 0}%
      </span>
    ),
  },
  {
    key: "description",
    label: "Description",
    render: (v) => (
      <span className="text-sm text-gray-600 max-w-xs truncate block">
        {v || "—"}
      </span>
    ),
  },
  {
    key: "approvalStatus",
    label: "Status",
    render: (v, row) => (
      <StatusBadge
        status={
          v === "pending"
            ? "pending"
            : v === "rejected"
              ? "rejected"
              : row.isDisable || row.active === false
                ? "inactive"
                : "active"
        }
        label={v === "pending" ? "Pending Approval" : undefined}
        dot
      />
    ),
  },
];

/* ==================== COMPONENT ==================== */

const HsnCode = () => {
  const dispatch = useDispatch();

  const list = useListPage({
    defaultPageSize: 20,
    defaultSortKey: "code",
    defaultSortDir: "asc",
  });

  const [hsnList, setHsnList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);

  // Dedicated status filter state
  const [statusFilter, setStatusFilter] = useState("");

  // Modal state
  const [modalMode, setModalMode] = useState(null); // null | "add" | "edit"
  const [editData, setEditData] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  /* ==================== STATUS FILTER HANDLER ==================== */

  const handleStatusFilterChange = useCallback(
    (key, value) => {
      // Support both (key, value) and direct value callbacks
      const selectedValue = value !== undefined ? value : key;

      setStatusFilter(
        selectedValue === null || selectedValue === undefined
          ? ""
          : String(selectedValue),
      );

      // Always start from the first page after filtering
      list.setPage(1);
    },
    [list.setPage],
  );

  /* ==================== CLEAR FILTERS ==================== */

  const handleClearFilters = useCallback(() => {
    setStatusFilter("");
    list.clearFilters();
    list.setPage(1);
  }, [list.clearFilters, list.setPage]);

  /* ==================== FETCH HSN LIST ==================== */

  const fetchList = useCallback(async () => {
    setLoading(true);

    try {
      const params = list.toQueryParams();

      const requestParams = {
        page: String(params.page || 1),
        size: String(params.limit || 20),
        keyWord: params.search || "",
      };

      // Only send the status parameter when a specific
      // status has been selected.
      // NOTE: toHsnListParams maps `active` → API; isDisable="false" means Active.
      if (statusFilter === "true") {
        requestParams.active = false; // Inactive → active: false
      } else if (statusFilter === "false") {
        requestParams.active = true; // Active   → active: true
      }

      const res = await dispatch(getHsnList(requestParams)).unwrap();

      const data = res?.data?.data || res?.data || {};

      const items = Array.isArray(data) ? data : data?.list || [];

      const totalCount = Number(data?.total ?? items.length);

      setHsnList(items);
      setTotal(totalCount);
    } catch (err) {
      toast.error(err?.message || "Failed to load HSN codes");
    } finally {
      setLoading(false);
    }
  }, [
    dispatch,
    list.page,
    list.pageSize,
    list.search,
    list.sortKey,
    list.sortDir,
    statusFilter,
    isRefresh,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  /* ==================== CLOSE MODAL ==================== */

  const closeModal = () => {
    setModalMode(null);
    setEditData(null);
  };

  /* ==================== CREATE / UPDATE ==================== */

  const handleHsnSubmit = async (values) => {
    const gstRate = Number(values.gstRate ?? values.IGST ?? 0);
    const cessRate = Number(values.cessRate ?? values.additionalTax ?? 0);
    const payload = {
      code: values.code.trim(),
      gstRate,
      cessRate,
      IGST: gstRate,
      CGST: gstRate / 2,
      SGST: gstRate / 2,
      additionalTax: cessRate,
      description: values.description?.trim() || "",
      isDisable: values.isDisable,
    };

    try {
      let res;

      if (modalMode === "edit") {
        res = await dispatch(
          updateHsn({
            ...payload,
            _id: values._id,
          }),
        ).unwrap();
      } else {
        res = await dispatch(createHsn(payload)).unwrap();
      }

      if (res?.error) {
        toast.error(res.error);
        return false;
      }

      toast.success(
        res?.message ||
          `HSN code ${modalMode === "edit" ? "updated" : "created"}`,
      );

      closeModal();

      setIsRefresh((r) => !r);
      return true;
    } catch (err) {
      toast.error(err?.message || "Save failed");
      return false;
    }
  };

  /* ==================== ENABLE / DISABLE ==================== */

  const handleToggleStatus = useCallback(
    async (row) => {
      try {
        const res = await dispatch(
          enableDisableHsn({
            _id: [row._id],
            isDisable: !row.isDisable,
          }),
        ).unwrap();

        toast.success(res?.message || "Status updated");

        setIsRefresh((r) => !r);
      } catch (err) {
        toast.error(err?.message || "Failed to update status");
      }
    },
    [dispatch],
  );

  const handleApprove = useCallback(
    async (row) => {
      try {
        const res = await dispatch(
          reviewHsnSubmission({
            _id: row._id,
            code: row.code,
            action: "approve",
          }),
        ).unwrap();
        toast.success(res?.message || "HSN code approved");
        setIsRefresh((value) => !value);
      } catch (err) {
        toast.error(err?.message || "Failed to approve HSN code");
      }
    },
    [dispatch],
  );

  /* ==================== DELETE ==================== */

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      const res = await dispatch(
        softDeleteHsn({
          _id: [deleteTarget._id],
        }),
      ).unwrap();

      toast.success(res?.message || "HSN code deleted");

      setDeleteOpen(false);
      setDeleteTarget(null);

      setIsRefresh((r) => !r);
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    }
  };

  /* ==================== ROW ACTIONS ==================== */

  const rowActions = useCallback(
    (row) => [
      ...(row.approvalStatus === "pending"
        ? [
            {
              label: "Approve",
              icon: <MdCheckCircle size={16} className="text-green-600" />,
              onClick: () => handleApprove(row),
            },
          ]
        : []),
      {
        label: "Edit",
        icon: <MdEdit size={16} className="text-blue-600" />,
        onClick: () => {
          setEditData({
            _id: row._id,
            code: row.code || "",
            gstRate:
              row.gstRate ??
              row.IGST ??
              Number(row.CGST || 0) + Number(row.SGST || 0),
            cessRate: row.cessRate ?? row.additionalTax ?? 0,
            description: row.description || "",
            isDisable: row.isDisable || false,
          });

          setModalMode("edit");
        },
      },
      {
        label: row.isDisable ? "Enable" : "Disable",
        icon: row.isDisable ? (
          <MdCheckCircle size={16} className="text-green-600" />
        ) : (
          <MdBlock size={16} className="text-amber-600" />
        ),
        onClick: () => handleToggleStatus(row),
        danger: !row.isDisable,
      },
      {
        label: "Delete",
        icon: <MdDelete size={16} className="text-red-600" />,
        onClick: () => {
          setDeleteTarget(row);
          setDeleteOpen(true);
        },
        danger: true,
      },
    ],
    [handleApprove, handleToggleStatus],
  );

  /* ==================== RENDER ==================== */

  return (
    <div>
      <PageHeader
        title="HSN Codes"
        subtitle="Manage Harmonized System Nomenclature codes and tax rates"
        breadcrumbs={[{ label: "Invoices & Taxation" }, { label: "HSN Codes" }]}
        actions={
          <PermissionGuard module="tax" action={ACTIONS.CREATE} hide>
            <button onClick={() => setModalMode("add")}>
              <MdAdd size={16} />
              Add HSN Code
            </button>
          </PermissionGuard>
        }
      />

      <DataTable
        columns={COLUMNS}
        data={hsnList}
        loading={loading}
        totalCount={total}
        page={list.page}
        pageSize={list.pageSize}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        onSearch={list.setSearch}
        onSort={list.setSort}
        sortKey={list.sortKey}
        sortDir={list.sortDir}
        onRefresh={fetchList}
        rowActions={rowActions}
        searchPlaceholder="Search HSN code or description…"
        emptyText="No HSN codes found."
        emptyIcon={<MdCode size={40} className="text-gray-200" />}
        requiredModule="tax"
        filterBar={
          <FilterBar
            filters={FILTER_FIELDS}
            values={{
              ...list.filters,
              isDisable: statusFilter,
            }}
            onChange={handleStatusFilterChange}
            onClear={handleClearFilters}
            loading={loading}
            activeCount={statusFilter !== "" ? 1 : 0}
          />
        }
      />

      {/* ==================== ADD / EDIT HSN MODAL (Shared) ==================== */}

      <AddHsnModal
        isOpen={Boolean(modalMode)}
        resetForm={closeModal}
        handleSubmit={handleHsnSubmit}
        mode={modalMode || "add"}
        initialData={editData}
        showActive={true}
      />

      {/* ==================== DELETE CONFIRMATION ==================== */}

      <ConfirmModal
        isOpen={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete HSN Code"
        message={`Delete HSN code "${deleteTarget?.code}"? This cannot be undone.`}
        variant="danger"
        confirmText="Delete"
      />
    </div>
  );
};

export default HsnCode;
